import {isEmpty, isEqual} from 'es-toolkit/compat';

import {gemStore} from 'app/gemStore';
import {HashHistory} from 'app/utils/urlState';
import {ComponentUrlState, NewState} from 'app/utils/urlStateElements/interfaces';

/**
 * Put back the state obj to hash url
 * @param newState
 * @return Hash url
 */
export function urlify(newState: NewState): string {
    return Object.keys(newState).reduce((urlPath: string, sliceKey: string) => {
        const state = newState[sliceKey];
        if (!state) {
            return urlPath;
        }

        const params = Object.keys(state).reduce((prevUrlParams: string, paramKey: string) => {
            const value = state[paramKey];
            if (value !== undefined && value !== null) {
                return `${prevUrlParams}/${paramKey}/${value}`;
            }

            return prevUrlParams;
        }, '');

        return `${urlPath}/${sliceKey}${params}`;
    }, '');
}

/**
 * Create hash with single component state.
 * @param hashKey
 * @param params
 */
export function createHashUrl(hashKey: string, params: ComponentUrlState): string {
    const hashStore: NewState = {
        [hashKey]: params,
    };

    return `#${urlify(hashStore)}`;
}

/**
 * Remove a single component state from url hash string
 * @param newState
 * @param stateKey
 * @param path
 */
export function removeStatePartFromHash(newState: NewState, stateKey: string, path: string): string {
    let urlPath = path;
    const pathParts = ['', stateKey];
    const data = newState[stateKey];
    if (data !== undefined) {
        Object.entries(data).forEach((pair) => {
            pathParts.push(...pair);
        });
    }

    urlPath = urlPath.replace(pathParts.join('/'), '');
    return urlPath;
}

export function updateUrlState<ComponentState>(cId: string, newState?: ComponentState, isPartialState = false): void {
    const fullHashState: NewState = {...(gemStore.getState().urlStateComponent.hashState ?? {})};

    if (fullHashState[cId] === undefined && isEmpty(newState)) {
        return;
    }

    const previousComponentHash = fullHashState[cId];

    if (isEqual(previousComponentHash, newState)) {
        return;
    }

    let newHashState: NewState;
    if (isPartialState) {
        const newComponentState = {...previousComponentHash, ...newState} as ComponentUrlState;
        const isStateEmpty = Object.keys(newComponentState).every((key) => newComponentState[key] === undefined);
        if (isStateEmpty) {
            delete fullHashState[cId];
            newHashState = fullHashState;
        } else {
            newHashState = {...fullHashState, [cId]: newComponentState};
        }
    } else if (isEmpty(newState)) {
        delete fullHashState[cId];
        newHashState = fullHashState;
    } else {
        newHashState = {...fullHashState, [cId]: newState as unknown as ComponentUrlState};
    }

    HashHistory.getInstance().pushAndDispatch(urlify(newHashState));
}
